import { helpdeskAPI } from '@api'

export const WEEK_DAYS = [1, 2, 3, 4, 5, 6, 7]

export const createWeekSchedule = (from = '09:00', to = '18:00', enabledDays = [1, 2, 3, 4, 5]) => WEEK_DAYS.map((day) => ({
  day,
  enabled: enabledDays.includes(day),
  intervals: enabledDays.includes(day) ? [{ from, to }] : [],
}))

export const SYSTEM_STATUSES = [
  { code: 'new', title: 'Новый' },
  { code: 'work', title: 'В работе' },
  { code: 'closed', title: 'Закрыт' },
]

export const DEFAULT_HELPDESK_SETTINGS = {
  isTechSupportEnabled: false,
  techSupportText: '',
  useHelpdeskSchedule: false,
  helpdeskSchedule: createWeekSchedule('08:00', '21:00'),
  afterHoursMessage: '',
  sendClosedTicketMessage: false,
  closedTicketMessage: '',
  requestSupportRating: false,
  ticketTypeSelectionText: '',
  helpdeskSoonOverduePercent: 80,
  linearDistribution: false,
  lines: [],
  ticketTypes: [],
  statuses: [],
  rules: [],
  managers: [],
  systemStatuses: SYSTEM_STATUSES,
}

const normalizePercent = (value) => {
  const number = Number(value)
  if (!Number.isFinite(number)) return DEFAULT_HELPDESK_SETTINGS.helpdeskSoonOverduePercent
  return Math.min(99, Math.max(1, Math.round(number)))
}

export const normalizeSchedule = (schedule, from = '09:00', to = '18:00', enabledDays = []) => {
  const source = Array.isArray(schedule) ? schedule : []
  return WEEK_DAYS.map((day) => {
    const saved = source.find((item) => Number(item.day) === day)
    if (saved) return {
      day,
      enabled: Boolean(saved.enabled),
      intervals: Array.isArray(saved.intervals) ? saved.intervals : [],
    }
    const enabled = enabledDays.includes(day)
    return { day, enabled, intervals: enabled ? [{ from, to }] : [] }
  })
}

const toArray = (value) => Array.isArray(value) ? value : []
const toIdList = (value) => toArray(value).map(String)

/**
 * Backend хранит настройки в своих именах (title/customStatuses/distributionRules/entityIds),
 * frontend работает с name/statuses/rules/entityValues.
 */
export const mapSettingsFromApi = (raw) => {
  const source = raw && typeof raw === 'object' ? raw : {}

  return {
    ...DEFAULT_HELPDESK_SETTINGS,
    isTechSupportEnabled: Boolean(source.isTechSupportEnabled),
    techSupportText: source.techSupportText ?? '',
    useHelpdeskSchedule: Boolean(source.useHelpdeskSchedule),
    helpdeskSchedule: normalizeSchedule(source.helpdeskSchedule, '08:00', '21:00'),
    afterHoursMessage: source.afterHoursMessage ?? '',
    sendClosedTicketMessage: Boolean(source.sendClosedTicketMessage),
    closedTicketMessage: source.closedTicketMessage ?? '',
    requestSupportRating: Boolean(source.requestSupportRating),
    ticketTypeSelectionText: source.ticketTypeSelectionText ?? source.ticket_type_selection_text ?? '',
    helpdeskSoonOverduePercent: normalizePercent(source.helpdeskSoonOverduePercent),
    linearDistribution: Boolean(source.linearDistribution),
    lines: toArray(source.lines).map((line, index) => ({
      id: String(line.id),
      name: line.title ?? line.name ?? '',
      sortOrder: Number(line.sortOrder ?? (index + 1) * 100),
    })),
    ticketTypes: toArray(source.ticketTypes).map((type) => ({
      id: String(type.id),
      name: type.title ?? type.name ?? '',
      priority: type.priority || 'medium',
      firstResponseMinutes: Number(type.firstResponseMinutes) || 10,
      closeMinutes: Number(type.closeMinutes) || 40,
      autoTransferMinutes: type.autoTransferMinutes ?? null,
      askUserToSelectOnCreate: Boolean(type.askUserToSelectOnCreate ?? type.ask_user_to_select_on_create),
      selectionNumber: type.selectionNumber ?? type.selection_number ?? '',
      isDefault: Boolean(type.isDefault),
    })),
    statuses: toArray(source.customStatuses).map((status, index) => ({
      id: String(status.id),
      name: status.title ?? status.name ?? '',
      sortOrder: Number(status.sortOrder ?? (index + 4) * 100),
      pauseSla: Boolean(status.pauseSla),
      automationType: status.automationType || status.automation_type || 'none',
      automationMinutes: status.automationMinutes ?? status.automation_minutes ?? '',
    })),
    rules: toArray(source.distributionRules).map((rule, index) => ({
      id: String(rule.id),
      name: rule.title ?? rule.name ?? '',
      ticketTypeId: String(rule.ticketTypeId ?? rule.ticket_type_id ?? 'all'),
      entityType: rule.entityType || 'course',
      entityValues: toIdList(rule.entityIds).length ? toIdList(rule.entityIds) : ['all'],
      sortOrder: Number(rule.sortOrder ?? (index + 1) * 100),
    })),
    managers: toArray(source.managers).map((manager) => ({
      id: String(manager.id),
      employeeId: Number(manager.employeeId) || null,
      employeeName: manager.employeeName ?? '',
      lineIds: toIdList(manager.lineIds),
      ruleIds: toIdList(manager.ruleIds),
      schedule: normalizeSchedule(manager.schedule),
      workStatus: manager.workStatus === 'pause' ? 'pause' : 'work',
    })),
    systemStatuses: toArray(source.systemStatuses).length
      ? toArray(source.systemStatuses).map((status) => ({ code: String(status.code), title: status.title }))
      : SYSTEM_STATUSES,
  }
}

export const mapSettingsToApi = (settings) => ({
  isTechSupportEnabled: Boolean(settings.isTechSupportEnabled),
  techSupportText: settings.techSupportText ?? '',
  useHelpdeskSchedule: Boolean(settings.useHelpdeskSchedule),
  helpdeskSchedule: normalizeSchedule(settings.helpdeskSchedule, '08:00', '21:00'),
  afterHoursMessage: settings.afterHoursMessage ?? '',
  sendClosedTicketMessage: Boolean(settings.sendClosedTicketMessage),
  closedTicketMessage: settings.closedTicketMessage ?? '',
  requestSupportRating: Boolean(settings.requestSupportRating),
  ticketTypeSelectionText: settings.ticketTypeSelectionText ?? '',
  ticket_type_selection_text: settings.ticketTypeSelectionText ?? '',
  helpdeskSoonOverduePercent: normalizePercent(settings.helpdeskSoonOverduePercent),
  linearDistribution: Boolean(settings.linearDistribution),
  lines: toArray(settings.lines).map((line, index) => ({
    id: line.id,
    title: line.name,
    sortOrder: Number(line.sortOrder ?? (index + 1) * 100),
  })),
  ticketTypes: toArray(settings.ticketTypes).map((type) => ({
    id: type.id,
    title: type.name,
    priority: type.priority || 'medium',
    firstResponseMinutes: Number(type.firstResponseMinutes) || 0,
    closeMinutes: Number(type.closeMinutes) || 0,
    autoTransferMinutes: type.autoTransferMinutes === '' || type.autoTransferMinutes === null || type.autoTransferMinutes === undefined
      ? null
      : Number(type.autoTransferMinutes),
    askUserToSelectOnCreate: Boolean(type.askUserToSelectOnCreate),
    ask_user_to_select_on_create: Boolean(type.askUserToSelectOnCreate),
    selectionNumber: type.selectionNumber === '' || type.selectionNumber === null || type.selectionNumber === undefined
      ? null
      : Number(type.selectionNumber),
    selection_number: type.selectionNumber === '' || type.selectionNumber === null || type.selectionNumber === undefined
      ? null
      : Number(type.selectionNumber),
    isDefault: Boolean(type.isDefault),
  })),
  customStatuses: toArray(settings.statuses).map((status, index) => ({
    id: status.id,
    title: status.name,
    sortOrder: Number(status.sortOrder ?? (index + 4) * 100),
    pauseSla: Boolean(status.pauseSla),
    automationType: status.automationType || 'none',
    automationMinutes: (status.automationType || 'none') === 'student_silent'
      ? Number(status.automationMinutes) || null
      : null,
  })),
  distributionRules: toArray(settings.rules).map((rule, index) => ({
    id: rule.id,
    title: rule.name,
    ticketTypeId: rule.ticketTypeId || 'all',
    ticket_type_id: rule.ticketTypeId || 'all',
    entityType: rule.entityType,
    entityIds: toIdList(rule.entityValues).length ? toIdList(rule.entityValues) : ['all'],
    sortOrder: Number(rule.sortOrder ?? (index + 1) * 100),
  })),
  // employeeName backend не хранит: это read-only поле, при PUT оно игнорируется.
  managers: toArray(settings.managers).map((manager) => ({
    id: manager.id,
    employeeId: Number(manager.employeeId),
    lineIds: toIdList(manager.lineIds),
    ruleIds: toIdList(manager.ruleIds),
    schedule: normalizeSchedule(manager.schedule),
    workStatus: manager.workStatus === 'pause' ? 'pause' : 'work',
  })),
})

const unwrap = (response) => response?.data?.data ?? response?.data ?? {}

export const fetchHelpdeskSettings = async () => mapSettingsFromApi(unwrap(await helpdeskAPI.getSettings()))

export const saveHelpdeskSettings = async (settings) =>
  mapSettingsFromApi(unwrap(await helpdeskAPI.saveSettings(mapSettingsToApi(settings))))

const pluralize = (value, forms) => {
  const rest100 = value % 100
  const rest10 = value % 10
  if (rest100 >= 11 && rest100 <= 14) return forms[2]
  if (rest10 === 1) return forms[0]
  if (rest10 >= 2 && rest10 <= 4) return forms[1]
  return forms[2]
}

/**
 * Длительность словами: «14 часов», «11 часов 15 минут», «45 минут».
 */
export const formatMinutesLong = (minutes) => {
  const total = Math.max(0, Math.round(Number(minutes) || 0))
  const hours = Math.floor(total / 60)
  const rest = total % 60

  if (!total) return '0 минут'

  const parts = []
  if (hours) parts.push(`${hours} ${pluralize(hours, ['час', 'часа', 'часов'])}`)
  if (rest) parts.push(`${rest} ${pluralize(rest, ['минута', 'минуты', 'минут'])}`)

  return parts.join(' ')
}

export const getTodaySchedule = (schedule = []) => {
  const today = new Date().getDay() || 7
  const item = toArray(schedule).find((day) => Number(day.day) === today)
  if (!item?.enabled) return 'Нерабочий день'

  const intervals = (item.intervals || []).map((interval) => `${interval.from}–${interval.to}`).join(', ')
  if (!intervals) return 'Не задано'

  // Рядом с интервалами показываем суммарную длительность смены.
  const minutes = getScheduleMinutesForDay(schedule, today)

  return minutes ? `${intervals} (${formatMinutesLong(minutes)})` : intervals
}

export const getScheduleMinutesForDay = (schedule, weekDay) => {
  const item = toArray(schedule).find((day) => Number(day.day) === weekDay)
  if (!item?.enabled) return 0

  return (item.intervals || []).reduce((total, interval) => {
    const [fromHours, fromMinutes] = String(interval.from || '').split(':').map(Number)
    const [toHours, toMinutes] = String(interval.to || '').split(':').map(Number)
    if (![fromHours, fromMinutes, toHours, toMinutes].every(Number.isFinite)) return total
    const diff = (toHours * 60 + toMinutes) - (fromHours * 60 + fromMinutes)
    return total + (diff > 0 ? diff : diff + 24 * 60)
  }, 0)
}
